import React from 'react';
import { useFieldArray } from 'react-hook-form';
import styled from 'styled-components';
import { defaultLogicOp, defaultPredicate } from '../../types';
import useFilterWidgetFormContext from '../../utils/useFilterWidgetFormContext';
import FilterWidgetPredicate from './FilterWidgetPredicate';
import { borderedContent } from '../../../../styles/shareableStyles';

/**
 * Each condition of Filter Widget consists of predicates and logicOps
 * which are both arrays.
 *
 * Amount of logicOps is always equal to: (amount of predicates - 1).
 */

interface IProps {
  conditionIndex: number;
  conditionsLength: number;
  maxPredicates: number;
  onRemoveLast?: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  padding: 12px 4px 12px 12px;
  ${borderedContent};
`;

const FilterWidgetCondition = ({
  conditionIndex,
  conditionsLength,
  onRemoveLast,
  maxPredicates,
}: IProps) => {
  const { control } = useFilterWidgetFormContext();

  const {
    fields: predicates,
    append: addPredicate,
    remove: removePredicate,
    update: updatePredicate,
  } = useFieldArray({
    name: `conditions.${conditionIndex}.predicates`,
    control,
  });

  const { append: addLogicOp, remove: removeLogicOp } = useFieldArray({
    name: `conditions.${conditionIndex}.logicOps`,
    control,
  });

  const handleAddPredicate = () => {
    addPredicate(defaultPredicate);
    addLogicOp(defaultLogicOp);
  };

  const handleRemovePredicate = (predicateIndex: number) => {
    // remove entire condition in case if there was a single predicate in it
    if (predicates.length === 1) {
      onRemoveLast?.();
      return;
    }

    removePredicate(predicateIndex);

    if (predicates.length === 2) {
      removeLogicOp();
    } else {
      removeLogicOp(predicateIndex - 1);
    }
  };

  // Removing a Field will reset entire predicate
  // because predicate values rely on it
  const handleResetField = (predicateIndex: number) => {
    updatePredicate(predicateIndex, defaultPredicate);
  };

  const disableRemovePredicates =
    conditionsLength === 1 && predicates.length === 1;

  const allowMorePredicates = predicates.length < maxPredicates;

  return (
    <Container>
      {predicates.map((predicate, predicateIndex) => (
        <FilterWidgetPredicate
          key={predicate.id}
          conditionIndex={conditionIndex}
          predicateIndex={predicateIndex}
          onResetField={() => handleResetField(predicateIndex)}
          onRemove={() => handleRemovePredicate(predicateIndex)}
          disableRemove={disableRemovePredicates}
          allowMorePredicates={
            allowMorePredicates && predicateIndex === predicates.length - 1
          }
          onAddPredicate={handleAddPredicate}
          onUpdatePredicate={(newPredicate) =>
            updatePredicate(predicateIndex, newPredicate)
          }
        />
      ))}
    </Container>
  );
};

export default FilterWidgetCondition;
